import { useEffect } from "react";

interface Props {
  correct: boolean;
  title?: string;
  children?: React.ReactNode;
  onNext: () => void;
  onRetry?: () => void;
  nextLabel?: string;
}

/** Shared result panel shown after an exercise is attempted. */
export default function FeedbackPanel({ correct, title, children, onNext, onRetry, nextLabel = "Next" }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onNext();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onNext]);

  return (
    <div
      className={`mt-5 rounded-[12px] border px-4 py-3.5 animate-fade-in ${
        correct
          ? "border-success/40 bg-success/10"
          : "border-error/40 bg-error/10"
      }`}
    >
      <div className={`text-sm font-semibold mb-1 ${correct ? "text-success" : "text-error"}`}>
        {title ?? (correct ? "Correct" : "Not quite")}
      </div>
      <div className="text-[0.8125rem] text-text-secondary leading-relaxed space-y-1">{children}</div>
      <div className="flex gap-2 mt-3.5">
        {!correct && onRetry && (
          <button className="btn-quiet text-xs px-4 py-2" onClick={onRetry}>
            Try again
          </button>
        )}
        <button className="btn-primary text-xs px-5 py-2" onClick={onNext}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}
